import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ScrollArea } from './ui/scroll-area';
import type { NodeRef, RepertoireLine } from '../api/types';
import { useProposedApi, useProposedEnabled } from '../proposed/context';
import type { NodeCounts } from '../proposed/types';
import { INDICATOR_GLYPHS, INDICATOR_LABELS, nodeIndicator } from '../state/indicators';
import { nodeKey } from '../state/route';

export interface MoveHistoryProps {
  line: RepertoireLine | null;
  /** The ply (node index) currently being viewed. */
  currentPly: number;
  /** Navigate to the node at the given ply (0 = start). */
  onSelect: (ply: number) => void;
  /** Mutate the branch beginning with this move. */
  onLineAction?: (action: 'promote' | 'demote' | 'delete', ply: number) => void;
  actionBusy?: boolean;
}

function sans(line: RepertoireLine): string[] {
  return line.movesSan && line.movesSan.length === line.movesUci.length
    ? line.movesSan
    : line.movesUci.map(() => '…');
}

/** Branch/merge/bowtie glyph on a move row (spec §7.2); text/aria, not color. */
function MoveIndicator({
  counts,
  node,
}: {
  counts?: Map<string, NodeCounts>;
  node: NodeRef;
}): JSX.Element | null {
  const indicator = nodeIndicator(counts?.get(nodeKey(node.zobristHash, node.positionId)));
  if (indicator === null) return null;
  return (
    <span
      className="move-indicator"
      role="img"
      aria-label={INDICATOR_LABELS[indicator]}
      title={INDICATOR_LABELS[indicator]}
    >
      {INDICATOR_GLYPHS[indicator]}
    </span>
  );
}

/** Lichess-style dual-column move list (White | Black), rendered in SAN. */
export function MoveHistory({
  line,
  currentPly,
  onSelect,
  onLineAction,
  actionBusy = false,
}: MoveHistoryProps): JSX.Element {
  const historyRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ ply: number; san: string; x: number; y: number } | null>(null);
  useEffect(() => {
    const viewport = historyRef.current?.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]');
    const current = viewport?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!viewport || !current) return;
    const panel = viewport.getBoundingClientRect();
    const move = current.getBoundingClientRect();
    const delta = move.top < panel.top
      ? move.top - panel.top
      : move.bottom > panel.bottom ? move.bottom - panel.bottom : 0;
    if (delta === 0) return;
    // Scroll only the notation viewport; scrollIntoView also moves the page.
    viewport.scrollTo?.({
      top: viewport.scrollTop + delta,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [currentPly, line]);
  useEffect(() => {
    if (menu === null) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    const close = (event: Event) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      setMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenu(null);
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menu]);
  const proposedApi = useProposedApi();
  // Branch/merge glyphs are a mock-mode Wave-4 prototype (ADR-0013): real builds
  // have no counts source, so the indicators are omitted rather than fabricated.
  const indicatorsEnabled = useProposedEnabled();
  const countsQuery = useQuery({
    queryKey: ['proposed', 'lineCounts', line?.lineId],
    queryFn: () => proposedApi.getLineCounts(line as RepertoireLine),
    enabled: line !== null && indicatorsEnabled,
  });
  const counts = indicatorsEnabled ? countsQuery.data : undefined;

  if (line === null) {
    return (
      <section className="panel" aria-labelledby="move-history-title">
        <h2 id="move-history-title">Moves</h2>
        <p className="muted">No line selected.</p>
      </section>
    );
  }
  const moves = sans(line);
  const rows: {
    white: { san: string; ply: number } | null;
    black: { san: string; ply: number } | null;
  }[] = [];
  for (let i = 0; i < moves.length; i += 2) {
    rows.push({
      white: { san: moves[i], ply: i + 1 },
      black: i + 1 < moves.length ? { san: moves[i + 1], ply: i + 2 } : null,
    });
  }
  return (
    <section ref={historyRef} className="panel move-history" aria-labelledby="move-history-title">
      <h2 id="move-history-title">Moves</h2>
      <ScrollArea className="h-56" aria-label="Move notation">
        <table>
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">Move number</span>
              </th>
              <th scope="col">White</th>
              <th scope="col">Black</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                <td className="move-no muted">{r + 1}.</td>
                {([row.white, row.black] as const).map((cell, c) =>
                  cell ? (
                    <td key={c}>
                      <button
                        type="button"
                        className={cell.ply === currentPly ? 'current' : ''}
                        onClick={() => onSelect(cell.ply)}
                        onContextMenu={(event) => {
                          if (!onLineAction) return;
                          event.preventDefault();
                          setMenu({
                            ply: cell.ply,
                            san: cell.san,
                            x: Math.min(event.clientX, window.innerWidth - 220),
                            y: Math.min(event.clientY, window.innerHeight - 164),
                          });
                        }}
                        onKeyDown={(event) => {
                          if (
                            !onLineAction ||
                            (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10'))
                          ) {
                            return;
                          }
                          event.preventDefault();
                          const bounds = event.currentTarget.getBoundingClientRect();
                          setMenu({
                            ply: cell.ply,
                            san: cell.san,
                            x: Math.min(bounds.left, window.innerWidth - 220),
                            y: Math.min(bounds.bottom, window.innerHeight - 164),
                          });
                        }}
                        aria-current={cell.ply === currentPly ? 'step' : undefined}
                        aria-haspopup={onLineAction ? 'menu' : undefined}
                        title={onLineAction ? 'Right-click for line actions' : undefined}
                      >
                        {cell.san}
                        <MoveIndicator counts={counts} node={line.nodes[cell.ply]} />
                      </button>
                    </td>
                  ) : (
                    <td key={c} />
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
      {menu !== null && (
        <div
          ref={menuRef}
          className="move-context-menu"
          role="menu"
          aria-label={`${menu.san} move actions`}
          style={{ left: menu.x, top: menu.y }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={actionBusy}
            onClick={() => {
              onLineAction?.('promote', menu.ply);
              setMenu(null);
            }}
          >
            Make main line
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={actionBusy}
            onClick={() => {
              onLineAction?.('demote', menu.ply);
              setMenu(null);
            }}
          >
            Make variation
          </button>
          <button
            type="button"
            role="menuitem"
            className="danger"
            disabled={actionBusy}
            onClick={() => {
              onLineAction?.('delete', menu.ply);
              setMenu(null);
            }}
          >
            Delete move and following
          </button>
        </div>
      )}
    </section>
  );
}
